import { addDays, format, differenceInDays, isWeekend } from 'date-fns';

export interface MockTransaction {
    id: string;
    date: string; // ISO date
    amount: number;
    type: 'revenue' | 'expense';
    category: string;
    entityId: string;
    description: string;
    source: string;
    status: 'completed' | 'refunded' | 'pending';
}

export interface MockEntity {
    id: string;
    name: string;
    type: string;
}

export interface MockEvent {
    id: string;
    date: string;
    name: string;
    type: string;
    impact: 'high' | 'medium' | 'low';
}

export interface MockData {
    transactions: MockTransaction[];
    entities: MockEntity[];
    events: MockEvent[];
    summary: {
        totalRevenue: number;
        totalExpenses: number;
        netIncome: number;
        transactionCount: number;
    };
}

export interface MockDataOptions {
    startDate: Date;
    endDate: Date;
    currency: string;
    seed?: number;
}

const CATEGORIES = {
    revenue: [
        'product_sale',
        'shipping',
        'tax',
        'Services',
        'Subscriptions',
        'upsell'
    ],
    expense: [
        'discount',
        'product_return',
        'shipping_refund',
        'Marketing',
        'Payroll',
        'Software',
        'Rent',
        'Office Supplies',
        'Server Costs',
        'Sales Commission'
    ]
};

const SOURCES = ['Shopify', 'Stripe', 'PayPal', 'Amazon', 'Direct'];
const ENTITY_NAMES = ['Acme Corp', 'Globex', 'Soylent Corp', 'Initech', 'Umbrella Corp', 'Stark Ind', 'Cyberdyne'];
const EVENT_TYPES = ['Marketing Campaign', 'Product Launch', 'Holiday', 'Server Outage', 'Market Shift'];

export const generateMockData = (options: MockDataOptions): MockData => {
    const { startDate, endDate } = options;
    const days = differenceInDays(endDate, startDate) + 1;

    // 1. Generate Entities
    const entities: MockEntity[] = ENTITY_NAMES.map((name, i) => ({
        id: `entity-${i}`,
        name,
        type: i % 2 === 0 ? 'Customer' : 'Vendor'
    }));

    const transactions: MockTransaction[] = [];
    const events: MockEvent[] = [];

    // Generate Events first so they can impact transactions
    const eventCount = Math.max(1, Math.floor(days / 10));
    for (let i = 0; i < eventCount; i++) {
        const eventDate = addDays(startDate, Math.floor(Math.random() * days));
        events.push({
            id: `evt-${i}`,
            date: format(eventDate, 'yyyy-MM-dd'),
            name: `${EVENT_TYPES[Math.floor(Math.random() * EVENT_TYPES.length)]}`,
            type: 'Milestone',
            impact: Math.random() > 0.7 ? 'high' : 'medium'
        });
    }

    let totalRevenue = 0;
    let totalExpenses = 0;

    // Trend parameters
    const trendSlope = (Math.random() - 0.2) * 5; // Slight upward trend bias
    const volatility = 0.4;

    for (let i = 0; i < days; i++) {
        const currentDate = addDays(startDate, i);
        const dateStr = format(currentDate, 'yyyy-MM-dd');

        // Check for active events on this day or +/- 2 days
        const nearbyEvents = events.filter(e => {
            const eDate = new Date(e.date);
            const diff = Math.abs(differenceInDays(currentDate, eDate));
            return diff <= 2;
        });

        const eventMultiplier = nearbyEvents.reduce((acc, e) => acc + (e.impact === 'high' ? 0.5 : 0.2), 1);
        const weekendMultiplier = isWeekend(currentDate) ? 1.3 : 1;
        const trendMultiplier = 1 + (i * trendSlope / 100);

        // Daily volume
        const baseTxCount = 10;
        const dailyTxCount = Math.floor((Math.random() * 10 + baseTxCount) * eventMultiplier * weekendMultiplier);

        for (let j = 0; j < dailyTxCount; j++) {
            const isRevenue = Math.random() > 0.45;
            const type = isRevenue ? 'revenue' : 'expense';
            const categories = CATEGORIES[type];
            let category = categories[Math.floor(Math.random() * categories.length)];

            // Logic for specific "required" categories
            if (isRevenue && Math.random() > 0.3) category = 'product_sale';
            if (!isRevenue && Math.random() > 0.8) category = 'discount';
            if (!isRevenue && Math.random() > 0.9) category = 'product_return';

            const entity = entities[Math.floor(Math.random() * entities.length)];
            const source = SOURCES[Math.floor(Math.random() * SOURCES.length)];

            // Amount distribution
            let amount = Math.pow(10, Math.random() * 2 + 1.5); // 30 to 3000

            // Adjust based on category
            if (category === 'tax') amount = amount * 0.15;
            if (category === 'shipping') amount = amount * 0.05;
            if (category === 'discount') amount = amount * 0.1;

            amount = amount * trendMultiplier * eventMultiplier * (1 + (Math.random() - 0.5) * volatility);
            amount = Math.round(amount * 100) / 100;

            const status = (category === 'product_return' || category === 'shipping_refund') ? 'refunded' : 'completed';

            if (isRevenue && status === 'completed') totalRevenue += amount;
            else if (!isRevenue || status === 'refunded') totalExpenses += amount;

            transactions.push({
                id: `tx-${i}-${j}`,
                date: dateStr,
                amount,
                type,
                category,
                entityId: entity.id,
                description: `${category.replace(/_/g, ' ')} via ${source}`,
                source,
                status
            });
        }
    }

    return {
        transactions,
        entities,
        events,
        summary: {
            totalRevenue,
            totalExpenses,
            netIncome: totalRevenue - totalExpenses,
            transactionCount: transactions.length
        }
    };
};

